import { FABRICATE_WORLD_PLACEMENT_ITEM_TYPES, ftFabricateExpiryOverride, ftFabricateShouldGrant } from '../port/k3-rules/fabricate_on_cast.js';
import { slot } from '../port/k3-rules/access.js';
import { fabricateRows } from '../actions/ability-preconditions.js';
import { type JanusCtx, type Row, definitionNotHeld, handBack, itemField, record, upper } from './context.js';
import { grantInventory, type TimedInventoryDelta } from './timed-finish/inventory.js';
import { appendSpawnStatus, emptySpawnPlan, mergeSpawnPlans } from '../spawn/row-plan.js';
import { getStatusEffectDurationTimes } from './statuses/get-status-effect-duration-times.js';
import type { JanusInput } from './janus.js';
import { encounterField } from '../../shared/array-types.js';
import { phpJsonInt } from '../compile/php-json-values.js';
import { fabricationEnvironment, type FabricationReaders } from './fabrication-targetless.js';
export { fabricationAllowsTargetlessCast } from './fabrication-targetless.js';

export const SPOILING = 'F7A1C3B2-5D64-4E18-9A70-2C8B41D5E093';

/** Adoption and timed observers hold every output before resolution can draw. */
export function fabricationDefinitionIds(ability: Row, entries: FabricationReaders['entries']): string[] {
  const outputs = fabricateRows(entries(ability, 'fabricate'));
  return outputs.length ? [...new Set([...outputs.flatMap(row => [upper(row.uuid), upper(row.template)]).filter(Boolean), SPOILING])] : [];
}

/** Cast-time grants use the shared inventory initializer and typed spawn save, including immediate casts. */
export function fabricateOnCast(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row): void {
  const outputs = fabricateRows(ctx.read.entries(ability, 'fabricate'));
  if (!outputs.length) return;
  const env = fabricationEnvironment(ctx.read);
  if (ctx.counterspelled || !ftFabricateShouldGrant(ability, input.useAbilityVariable1, env)) return;
  // A fabricated enchantment lasts for its source duration, including older unflagged spell rows.
  const override = ftFabricateExpiryOverride(outputs.some(output => output.template)
    ? { ...ability, item_layer_types: { ...record(ability.item_layer_types), fabricate_expires_after_duration: 1 } } : ability, env);
  const writes: TimedInventoryDelta = { creates: [], updates: [], removals: [] }, plan = emptySpawnPlan();
  for (const output of outputs) {
    const definition = ctx.read.definition(upper(output.uuid));
    if (!definition) definitionNotHeld('fabricate:output-definition-not-held', output.uuid);
    if (FABRICATE_WORLD_PLACEMENT_ITEM_TYPES.includes(String(itemField(definition, 'item_type') ?? ''))) continue;
    const amount = phpJsonInt(override ? slot(override, 'int') : ctx.read.value(definition, 'spoilage_int'));
    const type = String(override ? slot(override, 'type') : ctx.read.value(definition, 'spoilage_type') ?? '').trim().toLowerCase();
    const expiry = amount >= 1 && ['seconds', 'rounds', 'minutes', 'hours', 'days', 'weeks', 'months', 'years'].includes(type)
      ? { item_layer_types: { duration_int: amount, duration_type: type } } : null;
    const clock = expiry ? ctx.read.campaignClock() : null;
    if (expiry && !clock) handBack('fabricate:campaign-clock-not-held');
    const ends = expiry ? getStatusEffectDurationTimes(ctx, expiry, caster) : null;
    const first = writes.creates.length;
    // Every expiring cohort needs its own inventory identity for its Spoiling status.
    grantInventory(ctx, caster, output.uuid, output.qty, output.template, { writes, separate: !!expiry });
    for (const row of writes.creates.slice(first)) {
      plan.writes.push({ table: 'cast_inventory', op: 'insert', row });
      if (ends && clock) {
        if (!ctx.read.definition(SPOILING)) definitionNotHeld('fabricate:spoilage-definition-not-held', SPOILING);
        const campaignId = encounterField(ctx.encounter, 'encounter_master_parent_type') === 'campaign'
          ? upper(encounterField(ctx.encounter, 'encounter_master_parent_id')) : '';
        appendSpawnStatus(plan, { uuid: ctx.read.uuid, clock: () => ({ campaignId,
          starts: [clock.year, clock.month, clock.day, clock.hour, clock.minute, clock.second].join('-') }) },
        String(row.inventory_id), String(row.inventory_id), SPOILING, '', { ...ends, connection: ctx.read.uuid() });
      }
    }
  }
  for (const update of writes.updates) plan.writes.push({ table: 'inventory', op: 'update',
    row: { inventory_id: update.inventory_id, ...update.changes }, before: update.before });
  ctx.spawnRows = mergeSpawnPlans(ctx.spawnRows, plan);
}
